import { inject } from '@angular/core';
import {
  Router,
  type Route,
  type RouterStateSnapshot,
  type UrlTree,
} from '@angular/router';
import { FEATURE_DATA_MANAGEMENT_ROUTES } from '@bizzdesign/feature-data-management';
import { HomeContainerComponent } from '@bizzdesign/feature-home';
import {
  defaultLayoutConfig,
  errorPageLayoutConfig,
  searchLayoutConfig,
} from '@bizzdesign/feature-layout';
import { FEATURE_POPULATIONS_ROUTES } from '@bizzdesign/feature-populations';
import { searchMatcher } from '@bizzdesign/feature-search';
import { TrialPopoverStore } from '@bizzdesign/feature-trial-experience';
import { baseRoutes, isAuthenticated } from '@bizzdesign/shared/utils';
import {
  AccessDeniedComponent,
  InternalServerErrorComponent,
  PageNotFoundComponent,
} from '@bizzdesign/ui-error-pages';

import {
  FEATURE_FRAME_OUTLET_ROUTES,
  FEATURE_FRAME_ROUTES,
  FEATURE_FRAME_VIEW_ROUTES,
} from '@bizzdesign/feature-frame';

const MCP_OAUTH_RELAY_KEY = 'mcp_oauth_callback_result';
const MCP_OAUTH_RETURN_URL_KEY = 'aiAssistant.mcpOauthReturnUrl';

/**
 * Belt-and-braces fallback for the MCP OAuth flow when Atlassian breaks
 * out of the popup and redirects the *main* window to the registered
 * redirect URI. The static HTML at `/auth/oauth/callback/index.html`
 * handles the popup case; this guard handles the case where the Angular
 * SPA itself is loaded with `?code=...&state=...` query params.
 *
 * - Relays any code/state/error to localStorage so a still-open chat
 *   panel in another tab (with a live `waitForCode` listener) can pick
 *   it up via the storage event.
 * - Restores the URL the user was on when they clicked Connect.
 */
function mcpOAuthCallbackRedirect(): boolean {
  const router = inject(Router);
  const params = new URLSearchParams(globalThis.location.search);
  const code = params.get('code');
  const state = params.get('state');
  const error = params.get('error');
  if (code !== null || error !== null) {
    try {
      localStorage.setItem(
        MCP_OAUTH_RELAY_KEY,
        JSON.stringify({
          type: 'mcp-admin-oauth-callback',
          code,
          state,
          error,
          ts: Date.now(),
        }),
      );
    } catch {
      // localStorage may be unavailable — best-effort relay.
    }
  }
  const returnUrl = sessionStorage.getItem(MCP_OAUTH_RETURN_URL_KEY);
  sessionStorage.removeItem(MCP_OAUTH_RETURN_URL_KEY);
  const target = returnUrl
    ? new URL(returnUrl, globalThis.location.origin).pathname
    : `/${baseRoutes.home}`;
  router.navigateByUrl(target);
  return false;
}

function blockExpiredTrialNavigation(
  _route: unknown,
  state: RouterStateSnapshot,
): boolean | UrlTree {
  const router = inject(Router);
  const trialPopoverStore = inject(TrialPopoverStore);

  if (!trialPopoverStore.isTrialExpired()) {
    return true;
  }

  const currentPath = state.url.split('?')[0] || '';
  const allowedPath = `/${baseRoutes.home}`;
  if (currentPath === allowedPath) {
    return true;
  }

  return router.createUrlTree([`/${baseRoutes.home}`]);
}

export const appRoutes: Route[] = [
  // Fallback for the MCP OAuth callback when it loads in the main window
  // (the static HTML at `auth/oauth/callback/index.html` handles the
  // popup case; this guard catches main-window-redirect scenarios).
  {
    path: 'auth/oauth/callback',
    canActivate: [mcpOAuthCallbackRedirect],
    children: [],
  },
  {
    path: '',
    canActivateChild: [isAuthenticated, blockExpiredTrialNavigation],
    children: [
      { path: '', redirectTo: baseRoutes.home, pathMatch: 'full' },
      {
        path: baseRoutes.home,
        pathMatch: 'full',
        component: HomeContainerComponent,
        data: {
          layoutConfig: defaultLayoutConfig,
        },
      },
      {
        matcher: searchMatcher,
        component: HomeContainerComponent,
        data: {
          layoutConfig: searchLayoutConfig,
        },
      },
      {
        path: baseRoutes.accessDenied + '/:replaceUrl',
        pathMatch: 'full',
        component: AccessDeniedComponent,
        data: {
          layoutConfig: 'latestConfig',
        },
      },
      {
        path: baseRoutes.internalServerError,
        pathMatch: 'full',
        component: InternalServerErrorComponent,
        data: {
          layoutConfig: 'latestConfig',
        },
      },
      {
        path: baseRoutes.pageNotFound + '/:replaceUrl',
        pathMatch: 'full',
        component: PageNotFoundComponent,
        data: {
          layoutConfig: 'latestConfig',
        },
      },
      {
        path: '',
        loadChildren: () =>
          import('@bizzdesign/feature-workspaces').then(
            (m) => m.FEATURE_WORKSPACES_ROUTES,
          ),
      },
      {
        path: '',
        loadChildren: () =>
          import('@bizzdesign/feature-workspace').then(
            (m) => m.FEATURE_WORKSPACE_ROUTES,
          ),
      },
      ...FEATURE_FRAME_OUTLET_ROUTES,
      ...FEATURE_FRAME_ROUTES,
      ...FEATURE_FRAME_VIEW_ROUTES,
      ...FEATURE_DATA_MANAGEMENT_ROUTES,
      ...FEATURE_POPULATIONS_ROUTES,
      {
        path: '**',
        component: PageNotFoundComponent,
        data: {
          layoutConfig: errorPageLayoutConfig,
        },
      },
    ],
  },
];
